import type { RoutingMode } from '../../config/site-config';

export interface ParsedLocation {
  /** Route path without trailing slash, e.g. `/platform`. */
  readonly path: string;
  /** Element id to scroll to, without `#`. */
  readonly anchor: string | null;
  readonly query: URLSearchParams;
  /** Raw query string without `?`. */
  readonly queryString: string;
}

const normalizePath = (path: string): string => path.replace(/\/+$/, '') || '/';

/** Path mode: `/platform?x=1#faq`. */
export function parsePathLocation(pathname: string, search: string, hash: string): ParsedLocation {
  const queryString = search.replace(/^\?/, '');
  return {
    path: normalizePath(pathname),
    anchor: hash.length > 1 ? decodeURIComponent(hash.slice(1)) : null,
    query: new URLSearchParams(queryString),
    queryString,
  };
}

/** Hash mode (single-file preview): `#/solutions?x=1#faq`. Returns null for plain in-page anchors (`#main`). */
export function parseHashLocation(hash: string): ParsedLocation | null {
  if (!hash || hash === '#' || hash === '#/')
    return { path: '/', anchor: null, query: new URLSearchParams(), queryString: '' };
  if (!hash.startsWith('#/')) return null;
  let rest = hash.slice(1);
  let anchor: string | null = null;
  const anchorIndex = rest.indexOf('#');
  if (anchorIndex >= 0) {
    anchor = rest.slice(anchorIndex + 1);
    rest = rest.slice(0, anchorIndex);
  }
  const queryIndex = rest.indexOf('?');
  const queryString = queryIndex >= 0 ? rest.slice(queryIndex + 1) : '';
  if (queryIndex >= 0) rest = rest.slice(0, queryIndex);
  return { path: normalizePath(rest), anchor, query: new URLSearchParams(queryString), queryString };
}

export function parseLocation(
  mode: RoutingMode,
  loc: Pick<Location, 'pathname' | 'search' | 'hash'> = location,
): ParsedLocation | null {
  return mode === 'path' ? parsePathLocation(loc.pathname, loc.search, loc.hash) : parseHashLocation(loc.hash);
}
